/**
 * Small Matomo service wrapper so consumers can import it directly.
 * Keeps the same shape as the Vue plugin's $matomo but is module-importable
 * which makes refactors and migration to Vue 3 simpler.
 *
 * The global `_paq` type is defined once in `src/plugins/matomo.ts`; this
 * file does **not** redeclare it to avoid conflicting ambient declarations.
 * TypeScript will merge the augmentation from the plugin automatically.
 */

// NOTE: don't redeclare `Window._paq` here.  Having two different
// declarations was the source of the previous build failure.

const hasPaq = () => typeof window !== 'undefined' && !!window._paq;

/** Time to let the tracker request leave before a full-page navigation. */
const TRACK_NAVIGATION_DELAY_MS = 300;

const matomoService = {
  trackEvent(category: string, action: string, name?: string, value?: number) {
    if (!hasPaq()) return;
    window._paq!.push(['trackEvent', category, action, name, value]);
  },

  trackGoal(goalId: number, revenue?: number) {
    if (!hasPaq()) return;
    window._paq!.push(['trackGoal', goalId, revenue]);
  },

  trackPageView(title?: string) {
    if (!hasPaq()) return;
    if (title) window._paq!.push(['setDocumentTitle', title]);
    window._paq!.push(['trackPageView']);
  },

  /**
   * Navigate after a tracking hit so the request is not cancelled by unload.
   * If Matomo is disabled (e.g. local dev), navigates immediately.
   */
  navigateAfterTrack(url: string) {
    if (!url) return;
    const go = () => {
      window.location.href = url;
    };
    if (!hasPaq()) {
      go();
      return;
    }
    window.setTimeout(go, TRACK_NAVIGATION_DELAY_MS);
  },
};

export default matomoService;
